import { CheckoutService } from '@/services';
import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator';

import { Amount } from '@/core/helpers';
import { Tracker } from '@/views/checkout/utilities';
import { Fee, WaitingQueueEvent } from '@/views/checkout/interfaces';
import { PaymentMethod } from '@/core/payment-methods/interfaces';
import { EVENT_ID, FAILURE_EVENT_IDS } from '@/constants/event-id';
import { PAYMENT_METHOD_ID } from '@/core/payment-methods/constants';
import { findPaymentMethod } from '@/core/payment-methods/utilities';
import { PAYMENT_WAITING_QUEUE_EVENTS } from '@/constants/waiting-queue';
import { StoredCard } from '@/views/checkout/components/payment-methods/payment-card/interfaces';
import { addStoredCard, updateStoredCard, updateStoredCardUsage } from '@/core/payment-card/utilities/stored-cards';
import { setDatadogChargeDetailsFlow } from '@/views/checkout/utilities/datadog';
import WaitingQueue from '../waiting-queue';
import { TokenData, ChargeDetails } from './interfaces';
import { getSurchargeAmount, hasSurcharge } from '@/views/checkout/utilities/surcharge';

@Component({
    data() {
        return {
            opened: false,
            requires3ds: false
        };
    },
    components: {
        WaitingQueue
    }
})
export default class ChargeDialog extends Vue {
    @Ref() readonly iframe!: HTMLIFrameElement;

    @Prop(Boolean)
    value: boolean;

    @Prop(Number)
    amount: number;

    @Prop()
    currencyCode: string;

    @Prop(Object)
    chargeDetails: ChargeDetails;

    @Prop(Object)
    tokenData: TokenData;

    @Prop()
    email: string;

    @Prop()
    fullName: string;

    @Prop()
    notes: string;

    @Prop()
    customerNotes: string;

    @Prop(Boolean)
    paymentCodeRequired: boolean;

    @Prop(Object)
    selectedMethod: PaymentMethod | null;
    
    @Prop()
    fees: Fee[];

    opened: boolean;
    requires3ds: boolean;
    private chargeToken: string;

    get paymentWaitingQueueEvents(): Array<WaitingQueueEvent> {
        return PAYMENT_WAITING_QUEUE_EVENTS;
    }

    @Watch('value')
    async valueChange(value: boolean) {
        this.opened = value;
        this.chargeToken = '';

        if (!value) {
            return;
        }

        this.$nextTick(async () => {
            Tracker.trackPaymentSubmitted(this.chargeDetails, this.tokenData, this.selectedMethod);

            if (this.selectedMethod?.id === PAYMENT_METHOD_ID.Klarna) {
                await this.chargeKlarna();
            } else if (this.selectedMethod?.id === PAYMENT_METHOD_ID.Payconiq) {
                await this.chargePayconiq();
            } else if (this.selectedMethod?.id === PAYMENT_METHOD_ID.Satispay) {
                await this.chargeSatispay();
            } else if (this.selectedMethod?.id === PAYMENT_METHOD_ID.Iris) {
                await this.chargeIris();
            } else if (this.selectedMethod?.id === PAYMENT_METHOD_ID.MobilePay) {
                await this.chargeMobilePay();
            } else if (this.selectedMethod?.id === PAYMENT_METHOD_ID.GiroPayDirect) {
                await this.chargeGiroPay();
            } else if (this.selectedMethod && !findPaymentMethod(this.selectedMethod?.id)?.tokenRequired) {
                await this.createTransactionPaymentMethod();
            } else if (!this.tokenData.chargeToken) {
                await this.createChargeToken();
            } else {
                await this.processTransaction(
                    this.tokenData.chargeToken,
                    this.tokenData.redirectToACSForm,
                    this.tokenData.correlationId
                );
            }
        });
    }

    mounted() {
        this.opened = this.value;
        this.chargeToken = '';
    }

    destroyed() {
        this.unregisterIframeListener();
    }

    trackWaitingQueueEvent(id) {
        Tracker.trackPaymentWaitingQueue(id);
    }

    private getIframeDocument() {
        return (
            this.iframe?.contentDocument || this.iframe?.contentWindow?.document
        );
    }

    applyCustomerNotes() {
        if (!this.customerNotes || this.paymentCodeRequired) {
            return;
        }

        if (!this.chargeDetails) {
            this.chargeDetails = {};
        }

        if (!this.chargeDetails.customer) {
            this.chargeDetails.customer = {
                notes: ''
            };
        }

        this.chargeDetails.customer.notes = this.customerNotes;
    }

    private async createChargeToken() {
        const { ref } = this.$route.query;

        const { dccAccepted, dccOptions } = this.chargeDetails;

        const amount = dccAccepted && dccOptions ? dccOptions.billingAmount : Amount.convertAmountToCents(this.amount);
        const currencyCode = dccAccepted && dccOptions ? dccOptions.convertedCurrencyCode : this.currencyCode;

        this.applyCustomerNotes();

        const { data, success, correlationId } = await CheckoutService.createChargeToken({
            orderCode: ref as string,
            amount,
            currencyCode,
            holderName: this.chargeDetails?.customer?.fullName,
            holderMail: this.chargeDetails?.customer?.email,
            ...this.chargeDetails,
            merchantTrns: this.paymentCodeRequired && this.customerNotes ? this.customerNotes : undefined
        });

        setDatadogChargeDetailsFlow('createChargeToken', this.chargeDetails, correlationId);

        if (success) {
            await this.processTransaction(data.chargeToken, data.redirectToACSForm, correlationId);
        } else if (FAILURE_EVENT_IDS.includes(+data.eventId)) {
            this.$emit('decline', +data.eventId);
        } else {
            await this.$router.push({
                name: 'fail',
                params: {
                    eventId: data.eventId,
                    message: data.message
                }
            });
        }
    }

    private async processTransaction(
        chargeToken: string,
        redirectToACSForm: string | null,
        correlationId?: string
    ) {
        this.chargeToken = chargeToken;

        if (redirectToACSForm) {
            Tracker.trackThreeDsInitiation(correlationId);

            this.registerIframeListener();

            this.requires3ds = true;

            this.$nextTick(() => {
                this.getIframeDocument()?.write(redirectToACSForm);
            });
        } else {
            await this.createTransaction();
        }
    }

    private async createTransaction() {
        const { ref } = this.$route.query;
        const email = this?.chargeDetails?.customer?.email || this?.tokenData?.email;

        this.requires3ds = false;

        this.unregisterIframeListener();

        const { dccAccepted, dccOptions } = this.chargeDetails;

        const dccInfo = dccOptions
            ? {
                sessionId: dccOptions.sessionId,
                accepted: !!dccAccepted
            } : undefined;

        const amount = dccAccepted && dccOptions ? dccOptions.billingAmount : Amount.convertAmountToCents(this.amount);
        const currencyCode = dccAccepted && dccOptions ? dccOptions.convertedCurrencyCode : this.currencyCode;
        const surchargeAmount = hasSurcharge(this.fees) ? getSurchargeAmount(this.fees) : undefined;

        this.applyCustomerNotes();

        const { data, success, correlationId, eventId } = await CheckoutService.charge({
            amount,
            currencyCode,
            chargeToken: this.chargeToken,
            customer: {
                notes: this.chargeDetails?.customer?.notes || this.notes,
                email,
                fullName: this.chargeDetails?.customer?.fullName || this.fullName
            },
            installments: this.chargeDetails?.installments || 0,
            orderCode: ref as string,
            tipAmount: 0,
            loyaltyInfo: {
                redeemedAmount: this.chargeDetails.redeemedAmount,
                retrievalReferenceNumber: this.chargeDetails.retrievalReferenceNumber,
                offerCodes: this.chargeDetails.offerCodes
            },
            dccInfo,
            surchargeAmount,
            merchantTrns: this.paymentCodeRequired && this.customerNotes ? this.customerNotes : undefined
        });

        setDatadogChargeDetailsFlow('createTransaction', this.chargeDetails, correlationId);

        if (success && data.success) {
            const cardDetails = {
                email,
                id: data.creditCardId,
                requiresCvv: !!this.chargeDetails.cvc,
                cardHolder: this.chargeDetails.holderName,
                friendlyName: this.chargeDetails.friendlyName,
                maskedNumber: this.chargeDetails.maskedNumber,
                selectedBrand: this.chargeDetails.selectedBrand,
                expirationYear: this.chargeDetails.expirationYear,
                expirationMonth: this.chargeDetails.expirationMonth,
                loyaltySupported: this.chargeDetails.loyaltySupported
            } as StoredCard;

            if (this.chargeDetails?.rememberCard) {
                addStoredCard(cardDetails);
            }

            if (this.chargeDetails?.allowStoredCardUpdate && this.chargeDetails?.creditCardId) {
                updateStoredCard({
                    ...cardDetails,
                    id: this.chargeDetails?.creditCardId
                });
            }

            setDatadogChargeDetailsFlow('updatedSavedCardDetails', this.chargeDetails, correlationId);
        }

        if (this.chargeDetails?.creditCardId && success && data.success) {
            updateStoredCardUsage(this.chargeDetails.creditCardId);
        }

        if (success && !data.success) {
            if (!this.chargeDetails.scaRequestedTransactionId &&
                !!eventId &&
                (+eventId === EVENT_ID.ActivityAccountLimitExceeded || +eventId === EVENT_ID.SoftDecline)
            ) {
                this.chargeDetails.scaRequestedTransactionId = data.transactionId;
                await this.createChargeToken();
                return;
            }
        }

        await this.chargeCallback(data, success, correlationId, eventId);
    }

    private async chargeKlarna() {
        const { ref } = this.$route.query;

        const { data, success, correlationId, eventId } = await CheckoutService.createKlarnaSession({
            orderCode: ref as string,
            amount: Amount.convertAmountToCents(this.amount),
            customerEmail: this.chargeDetails?.customer?.email || this.email,
            customerName: this.chargeDetails?.customer?.fullName || this.fullName,
            refererUrl: this.chargeDetails?.refererUrl,
            merchantTrns: this.paymentCodeRequired && this.customerNotes ? this.customerNotes : undefined
        });

        await this.chargeCallback(data, success, correlationId, eventId);
    }

    private async chargePayconiq() {
        const { ref } = this.$route.query;

        const { data, success, correlationId, eventId } = await CheckoutService.createPayconiqSession({
            orderCode: ref as string,
            amount: Amount.convertAmountToCents(this.amount),
            customerEmail: this.chargeDetails?.customer?.email || this.email,
            customerName: this.chargeDetails?.customer?.fullName || this.fullName,
            refererUrl: this.chargeDetails?.refererUrl,
            merchantTrns: this.paymentCodeRequired && this.customerNotes ? this.customerNotes : undefined
        });

        await this.chargeCallback(data, success, correlationId, eventId);
    }

    private async chargeIris() {
        const { ref } = this.$route.query;

        this.applyCustomerNotes();

        const { data, success, correlationId, eventId } = await CheckoutService.createIrisSession({
            orderCode: ref as string,
            amount: Amount.convertAmountToCents(this.amount),
            customerEmail: this.chargeDetails?.customer?.email || this.email,
            customerName: this.chargeDetails?.customer?.fullName || this.fullName,
            customerTrns: this.chargeDetails?.customer?.notes || this.notes,
            merchantTrns: this.paymentCodeRequired && this.customerNotes ? this.customerNotes : undefined
        });

        await this.chargeCallback(data, success, correlationId, eventId);
    }

    private async chargeMobilePay() {
        const { ref } = this.$route.query;

        const { data, success, correlationId, eventId } = await CheckoutService.createMobilePaySession({
            orderCode: ref as string,
            customerEmail: this.chargeDetails?.customer?.email || this.email,
            customerName: this.chargeDetails?.customer?.fullName || this.fullName,
            refererUrl: this.chargeDetails?.refererUrl,
            merchantTrns: this.paymentCodeRequired && this.customerNotes ? this.customerNotes : undefined
        });

        await this.chargeCallback(data, success, correlationId, eventId);
    }

    private async chargeSatispay() {
        const { ref } = this.$route.query;

        const { data, success, correlationId, eventId } = await CheckoutService.createSatispaySession({
            orderCode: ref as string,
            amount: Amount.convertAmountToCents(this.amount),
            customerEmail: this.chargeDetails?.customer?.email || this.email,
            customerName: this.chargeDetails?.customer?.fullName || this.fullName,
            merchantTrns: this.paymentCodeRequired && this.customerNotes ? this.customerNotes : undefined
        });

        await this.chargeCallback(data, success, correlationId, eventId);
    }

    private async chargeGiroPay() {
        const { ref } = this.$route.query;

        const { data, success, correlationId, eventId } = await CheckoutService.createGiroPaySession({
            orderCode: ref as string,
            merchantTrns: this.paymentCodeRequired && this.customerNotes ? this.customerNotes : undefined
        });

        await this.chargeCallback(data, success, correlationId, eventId);
    }

    private async createTransactionPaymentMethod() {
        const ref = this.$route.query.ref as string;
        const surchargeAmount = hasSurcharge(this.fees) ? getSurchargeAmount(this.fees) : undefined;

        this.applyCustomerNotes();

        const { data, success, correlationId, eventId } = await CheckoutService.charge({
            ...this.tokenData,
            surchargeAmount,
            paymentMethodId: this.selectedMethod?.id,
            amount: Amount.convertAmountToCents(this.amount),
            orderCode: ref as string,
            customer: {
                email: this.chargeDetails?.customer?.email || this.email,
                notes: this.chargeDetails?.customer?.notes || this.notes,
                fullName: this.chargeDetails?.customer?.fullName || this.fullName
            },
            walletId: this.chargeDetails?.walletId,
            oneTimePassword: this.chargeDetails?.oneTimePassword,
            iban: this.chargeDetails?.iban,
            merchantTrns: this.paymentCodeRequired && this.customerNotes ? this.customerNotes : undefined
        });

        await this.chargeCallback(data, success, correlationId, eventId);
    }

    trackPaymentResponse(data, success, paymentMethodId, correlationId, eventId) {
        Tracker.trackPaymentResponse(
            success && (data?.success ?? true),
            paymentMethodId,
            correlationId,
            eventId,
            data?.redirectUrl
        );
    }

    private async chargeCallback(data, success, correlationId, eventId) {
        if (FAILURE_EVENT_IDS.includes(+eventId)) {
            this.$emit('decline', +eventId);

            this.$nextTick(async () => {
                Tracker.trackProcessingFailure(+eventId, correlationId);
            });
        } else if (+eventId === EVENT_ID.SecurityValidateOtpInvalid) {
            this.$emit('validate:otp:fail', +eventId);
        } else if (success && data.loyalty) {
            this.$emit('loyalty:success', data);
        } else if (data.redirectUrl) {
            this.$nextTick(async () => {
                this.trackPaymentResponse(
                    data,
                    success,
                    this.tokenData?.paymentMethodId || this.selectedMethod?.id,
                    correlationId,
                    eventId
                );
            });

            window.location.href = data.redirectUrl;
        } else {
            await this.$router.push({
                name: success && (data.success ?? true) ? 'success' : 'fail',
                params: {
                    eventId: data.eventId,
                    dccAccepted: data.dccAccepted
                }
            });
        }
    }

    private registerIframeListener() {
        window.addEventListener('message', this.onIframeMessage);
    }

    private unregisterIframeListener() {
        window.removeEventListener('message', this.onIframeMessage);
    }

    private async onIframeMessage(e: MessageEvent) {
        if (!e?.data || typeof e.data !== 'string') {
            return;
        }

        try {
            const data: {
                Success: boolean;
                MessageType: string;
            } = JSON.parse(e.data);

            if (data.MessageType === 'vp_sc_threedsecure') {
                await this.createTransaction();
            }
        } catch (error) {
            console.log(error);
        }
    }
}
